import { reportClientError } from './clientErrorReporter';

const AUTH_SESSION_TAG_KEY = 'runai:auth-observability:session-tag';
export const SMS_LOGIN_STUCK_TIMEOUT_MS = 30_000;

type AuthObservabilityBase = {
  authState?: string;
  prevAuthState?: string;
  userId?: string;
  errorType?: string;
  errorMessage?: string;
  connectionState?: string;
  finalPath?: string;
  phoneNumber?: string;
  elapsedMs?: number;
  buildVersion?: string;
  nextExpectedStep?: string;
  dedupeKeySuffix?: string;
};

type SmsLoginStuckTimerParams = {
  authState: string;
  prevAuthState?: string;
  phoneNumber?: string;
  connectionState?: string;
  finalPath?: string;
  nextExpectedStep: string;
  attemptKey: string;
};

let smsLoginStuckTimer: ReturnType<typeof setTimeout> | undefined;

function buildRandomTag() {
  return Math.random().toString(36).slice(2, 10);
}

export function getAuthObservabilityFinalPath() {
  return globalThis.location?.pathname;
}

export function buildAuthSessionTag() {
  if (typeof window === 'undefined') {
    return 'auth-server';
  }

  const existing = window.sessionStorage.getItem(AUTH_SESSION_TAG_KEY);
  if (existing) {
    return existing;
  }

  const created = `auth-${buildRandomTag()}`;
  window.sessionStorage.setItem(AUTH_SESSION_TAG_KEY, created);
  return created;
}

export function maskPhoneForObservability(phoneNumber?: string) {
  const normalized = phoneNumber?.replace(/[^\d+]/g, '');
  if (!normalized) return undefined;

  const plus = normalized.startsWith('+') ? '+' : '';
  const digits = normalized.replace(/[^\d]/g, '');
  if (!digits) return undefined;

  const head = digits.slice(0, 1);
  const tail = digits.slice(-3);
  const maskedCount = Math.max(0, digits.length - head.length - tail.length);
  return `${plus}${head}${'*'.repeat(maskedCount)}${tail}`;
}

function buildMetadata(source: 'auth-session' | 'sms-login', transition: string, extra: AuthObservabilityBase) {
  return {
    source,
    transition,
    lane: 'webchat-auth',
    sessionTag: buildAuthSessionTag(),
    authState: extra.authState,
    prevAuthState: extra.prevAuthState,
    userId: extra.userId,
    phoneMask: maskPhoneForObservability(extra.phoneNumber),
    errorType: extra.errorType,
    errorMessage: extra.errorMessage,
    connectionState: extra.connectionState,
    finalPath: extra.finalPath,
    elapsedMs: extra.elapsedMs,
    buildVersion: extra.buildVersion,
    nextExpectedStep: extra.nextExpectedStep,
  };
}

export function reportAuthSessionEvent(transition: string, extra: AuthObservabilityBase = {}) {
  const metadata = buildMetadata('auth-session', transition, extra);
  reportClientError({
    kind: 'runtime',
    mode: 'immediate',
    message: `[auth-session] ${transition}`,
    metadata,
    dedupeKey: [
      metadata.source,
      metadata.transition,
      metadata.sessionTag || '',
      metadata.authState || '',
      metadata.prevAuthState || '',
      metadata.userId || '',
      metadata.finalPath || '',
      extra.dedupeKeySuffix || '',
    ].join(':'),
  });
}

export function reportSmsLoginEvent(transition: string, extra: AuthObservabilityBase = {}) {
  const metadata = buildMetadata('sms-login', transition, extra);
  reportClientError({
    kind: 'runtime',
    mode: 'immediate',
    message: `[sms-login] ${transition}`,
    metadata,
    dedupeKey: [
      metadata.source,
      metadata.transition,
      metadata.sessionTag || '',
      metadata.authState || '',
      metadata.phoneMask || '',
      metadata.errorType || '',
      extra.dedupeKeySuffix || '',
    ].join(':'),
  });
}

export function clearSmsLoginStuckTimer() {
  if (smsLoginStuckTimer !== undefined) {
    clearTimeout(smsLoginStuckTimer);
    smsLoginStuckTimer = undefined;
  }
}

export function armSmsLoginStuckTimer(params: SmsLoginStuckTimerParams) {
  clearSmsLoginStuckTimer();

  const startedAt = Date.now();
  smsLoginStuckTimer = setTimeout(() => {
    smsLoginStuckTimer = undefined;
    reportSmsLoginEvent('stuck_at_auth_state', {
      authState: params.authState,
      prevAuthState: params.prevAuthState,
      phoneNumber: params.phoneNumber,
      connectionState: params.connectionState,
      finalPath: params.finalPath,
      elapsedMs: Date.now() - startedAt,
      nextExpectedStep: params.nextExpectedStep,
      dedupeKeySuffix: params.attemptKey,
    });
  }, SMS_LOGIN_STUCK_TIMEOUT_MS);
}
